import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Download, FileText, Loader2 } from 'lucide-react';
import { documentsApi, errorDetailText } from '../services/api';
import { Document } from '../types';
import { Badge, Button, Skeleton } from '../components/ui';

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Processing status, in words (spec §6). Uploads OCR lazily at first use,
 *  so an "uploaded" file is genuinely ready to attach. */
function statusChip(status: string) {
  if (status === 'failed') return <Badge tone="warning">Couldn't read</Badge>;
  if (status === 'processing') return <Badge tone="info">Reading the text…</Badge>;
  return <Badge tone="success">Ready</Badge>;
}

/**
 * The shared document viewer (`/app/documents/:id`) — metadata + status for one
 * uploaded or generated document. Case-file Documents rows link here.
 */
export function DocumentViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<(Document & { download_url?: string | null }) | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    let stale = false;
    documentsApi
      .getDocument(id)
      .then(({ data }) => {
        if (!stale) setDoc(data);
      })
      .catch((err: any) => {
        if (!stale) setError(errorDetailText(err, 'Failed to load the document'));
      });
    return () => {
      stale = true;
    };
  }, [id]);

  return (
    <div className="p-2 sm:p-6 max-w-3xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center space-x-2 text-legal-500 hover:text-legal-700 mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm">Back</span>
      </button>

      {error ? (
        <div className="flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      ) : !doc ? (
        <div className="space-y-4">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <div className="bg-white rounded-card border border-legal-200 p-5">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-legal-50 rounded-lg shrink-0">
              <FileText className="h-5 w-5 text-legal-500" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold text-legal-900 break-words">
                {doc.original_filename}
              </h1>
              <p className="text-xs text-legal-400 mt-1">
                {formatBytes(doc.file_size_bytes)} · uploaded{' '}
                {new Date(doc.created_at).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </p>
              <div className="mt-2.5">{statusChip(doc.processing_status)}</div>
              {doc.processing_status === 'failed' && (
                <p className="text-sm text-legal-500 mt-3">
                  We couldn't read this file's text. Upload it again — attach it while asking
                  a legal question or drafting, or from the All documents library — to try again.
                </p>
              )}
              {!isProcessing(doc.processing_status) && doc.processing_status !== 'failed' && (
                <p className="text-sm text-legal-500 mt-3">
                  This document is ready — attach it when asking a legal question or drafting a
                  reply, and its contents will be used.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            {doc.processing_status !== 'failed' && (
              <>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() =>
                    navigate('/app/research/new', {
                      state: { doc: { id: doc.id, name: doc.original_filename } },
                    })
                  }
                  data-track="document-research"
                >
                  Research this document
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() =>
                    navigate('/app/drafts/new', {
                      state: { doc: { id: doc.id, name: doc.original_filename } },
                    })
                  }
                  data-track="document-draft-reply"
                >
                  Draft a reply
                </Button>
              </>
            )}
            {doc.download_url && (
              <Button
                variant="secondary"
                size="md"
                href={doc.download_url}
                target="_blank"
                rel="noopener noreferrer"
                data-track="document-download"
              >
                <Download className="h-4 w-4" /> Download
              </Button>
            )}
            <Button variant="secondary" size="md" to="/app/documents" data-track="document-all">
              All documents
            </Button>
          </div>

          {isProcessing(doc.processing_status) && (
            <p className="flex items-center gap-2 text-xs text-legal-400 mt-4">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Still reading the text — check
              back in a moment.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function isProcessing(status: string) {
  return status === 'processing';
}
